import { create } from 'zustand';
import { checkInPoints, fetchCheckInStatus, fetchMembershipPlans } from '@/service/api/membership';
import { useAuthStore } from '@/stores/authStore';

export interface MembershipStoreState {
  isOpen: boolean;
  defaultTab: 'membership' | 'points';
  defaultMemberType: 'personal' | 'enterprise';
  openMembershipModal: (options?: {
    defaultTab?: 'membership' | 'points';
    defaultMemberType?: 'personal' | 'enterprise';
  }) => void;
  closeMembershipModal: () => void;

  // 套餐列表全局共享状态与 Action
  plans: Api.Membership.PlanResponse[];
  plansLoading: boolean;
  fetchPlans: (params?: Api.Membership.QueryPlansParams) => Promise<Api.Membership.PlanResponse[]>;
  getPlanById: (planId?: string) => Api.Membership.PlanResponse | undefined;

  // 每日签到全局共享状态与 Action
  checkInStatus: Api.Membership.CheckInResponse | null;
  isCheckInLoading: boolean;
  fetchCheckInStatus: () => Promise<Api.Membership.CheckInResponse | null>;
  doCheckIn: () => Promise<Api.Membership.CheckInResponse>;
}

export const useMembershipStore = create<MembershipStoreState>((set, get) => ({
  isOpen: false,
  defaultTab: 'membership',
  defaultMemberType: 'personal',
  openMembershipModal: (options) =>
    set({
      isOpen: true,
      defaultTab: options?.defaultTab ?? 'membership',
      defaultMemberType: options?.defaultMemberType ?? 'personal',
    }),
  closeMembershipModal: () => set({ isOpen: false }),

  // 套餐相关状态
  plans: [],
  plansLoading: false,

  // 获取会员套餐数据并存入 store
  fetchPlans: async (params) => {
    set({ plansLoading: true });
    try {
      if (params?.subject_type) {
        const res = await fetchMembershipPlans(params);
        const fetchedPlans = res?.plans || [];
        set((state) => {
          const map = new Map(state.plans.map((p) => [p.id, p]));
          fetchedPlans.forEach((p) => {
            if (p.id) map.set(p.id, p);
          });
          return { plans: Array.from(map.values()), plansLoading: false };
        });
        return fetchedPlans;
      } else {
        // 未提供 subject_type 时，同时拉取个人和企业套餐存入 store
        const [personalRes, enterpriseRes] = await Promise.allSettled([
          fetchMembershipPlans({ subject_type: 'PERSONAL' }),
          fetchMembershipPlans({ subject_type: 'ENTERPRISE' }),
        ]);
        const personalPlans = personalRes.status === 'fulfilled' ? personalRes.value?.plans || [] : [];
        const enterprisePlans = enterpriseRes.status === 'fulfilled' ? enterpriseRes.value?.plans || [] : [];
        const allFetched = [...personalPlans, ...enterprisePlans];
        set((state) => {
          const map = new Map(state.plans.map((p) => [p.id, p]));
          allFetched.forEach((p) => {
            if (p.id) map.set(p.id, p);
          });
          return { plans: Array.from(map.values()), plansLoading: false };
        });
        return allFetched;
      }
    } catch (err) {
      console.error('获取会员套餐列表失败:', err);
      set({ plansLoading: false });
      return [];
    }
  },

  getPlanById: (planId) => {
    if (!planId) return undefined;
    const { plans } = get();
    return plans.find((p) => p.id === planId || p.plan_code === planId);
  },

  // 签到相关状态
  checkInStatus: null,
  isCheckInLoading: false,

  // 查询每日签到状态
  fetchCheckInStatus: async () => {
    try {
      const res = await fetchCheckInStatus();
      if (res) {
        set({ checkInStatus: res });
      }
      return res;
    } catch (err) {
      console.error('获取签到状态失败:', err);
      return null;
    }
  },

  // 执行签到操作
  doCheckIn: async () => {
    set({ isCheckInLoading: true });
    try {
      const res = await checkInPoints();
      if (res) {
        set({ checkInStatus: res });
        // 签到成功后同步刷新全局账号及积分钱包
        void useAuthStore.getState().fetchCurrentAccount();
      }
      return res;
    } finally {
      set({ isCheckInLoading: false });
    }
  },
}));


